import { selectContact, selectContactGetter } from '@repo/redux/actions/contacts';
import { ID } from '@repo/utils/types';
import { ContactAvatar } from '@repo/components/avatars';
import { ListRowWithRef } from '@repo/components/lists';
import React, {
  useEffect,
  useMemo,
  useState,
  Dispatch,
  useContext,
  useRef,
  useCallback,
} from 'react';
import { useSelector } from 'react-redux';
import { VariableSizeList } from 'react-window';
import { Actions } from '@views/Activity/ActivityListItem/ItemActions/ItemActions';
import styles from './SidebarList.module.scss';
import { useAppContactSelecting, useHotkeyHandler, useIsOnScreen } from '@repo/hooks';
import Contact from '@repo/utils/models/Contact';
import { useNavigate } from 'react-router-dom';
import { isDownHandler, isUpHandler } from '@repo/utils/helpers/keyboardHandler';
import { SectionTitle } from '@repo/components/containers';

interface SidebarListState {
  rowIDs: ID[];
  activeContactID: ID | null;
  setActiveContact?: Dispatch<Contact>;
  scrollIntoView?: (idx: number) => void;
  lastSelectionEventType?: 'mouse' | 'keyboard';
  setLastSelectionEventType?: Dispatch<'mouse' | 'keyboard'>;
}

const SidebarListContext = React.createContext<SidebarListState>({
  rowIDs: [],
  activeContactID: null,
});

const ContactRow = ({ contactID, style, index }) => {
  const rowRef = useRef<HTMLDivElement>(null);
  const {
    activeContactID,
    setActiveContact,
    setLastSelectionEventType,
    lastSelectionEventType,
    scrollIntoView,
  } = useContext(SidebarListContext);
  const navigate = useNavigate();
  const contact = useSelector(selectContact(contactID));
  const selected = contactID === activeContactID;

  const isOnScreen = useIsOnScreen(rowRef, { rootMargin: '-87px 0px -50px 0px' });

  useEffect(() => {
    if (selected && lastSelectionEventType === 'keyboard' && !isOnScreen) {
      scrollIntoView(index < 6 ? 0 : index);
    }
  }, [selected, lastSelectionEventType]);

  return (
    <ListRowWithRef
      ref={rowRef}
      style={style}
      selected={selected}
      className={styles.contactRow}
      onMouseEnter={() => {
        setLastSelectionEventType('mouse');
        setActiveContact(contact);
      }}
      onClick={() => navigate(contact.link)}
    >
      <ContactAvatar contact={contact} size="sm" />
      <span className={styles.contactName}>{contact.displayName}</span>
      {selected && <Actions className={styles.contactActions} contact={contact} noDismissal />}
    </ListRowWithRef>
  );
};

const Row = ({ index, style }) => {
  const { rowIDs } = React.useContext(SidebarListContext);
  const id = rowIDs[index];

  if (id === 'header') {
    return (
      <div style={style} className={styles.header}>
        <SectionTitle underline>People</SectionTitle>
      </div>
    );
  }

  return <ContactRow style={style} contactID={id} index={index} />;
};

export default function SidebarList({
  contactIDs,
  defaultSelectedContactID,
}: {
  contactIDs: ID[];
  defaultSelectedContactID?: ID;
}) {
  const [height, setHeight] = useState(0);
  const navigate = useNavigate();
  const listRef = useRef<VariableSizeList<any>>(null);
  const contactGetter = useSelector(selectContactGetter);
  const firstContact = useSelector(selectContact(defaultSelectedContactID || contactIDs[0]));
  const [activeContact, setActiveContact] = useState(firstContact);
  const [lastSelectionEventType, setLastSelectionEventType] = useState<'mouse' | 'keyboard'>();

  const rowIDs = useMemo(() => {
    return ['header'].concat(contactIDs);
  }, [contactIDs]);

  const scrollIntoView = useCallback((idx) => {
    listRef.current?.scrollToItem(idx, 'center');
  }, []);

  useHotkeyHandler(
    {
      handler: (e) => {
        if (isDownHandler(e)) {
          const nextIndex = contactIDs.indexOf(activeContact.id) + 1;
          const nextRowID = contactIDs[nextIndex];
          if (nextRowID) {
            setLastSelectionEventType('keyboard');
            setActiveContact(contactGetter(nextRowID));
          }
        } else if (isUpHandler(e)) {
          const nextIndex = contactIDs.indexOf(activeContact.id) - 1;
          const nextRowID = contactIDs[nextIndex];

          if (nextRowID && nextRowID !== 'header') {
            setLastSelectionEventType('keyboard');
            setActiveContact(contactGetter(nextRowID));
          }
        } else if (e.key === 'Enter') {
          if (activeContact) {
            navigate(activeContact.link);
          }
        }
      },
    },
    [activeContact],
  );

  useAppContactSelecting({
    contact: activeContact,
    source: `map-annotation-cluster`,
  });

  useEffect(() => {
    if (listRef.current && defaultSelectedContactID) {
      const idx = contactIDs.indexOf(defaultSelectedContactID);
      if (idx > -1) {
        setTimeout(() => listRef.current.scrollToItem(idx, 'center'), 0);
      }
    }
  }, []);

  useEffect(() => {
    const updateHeight = () => {
      setHeight(window.innerHeight);
    };

    window.addEventListener('resize', updateHeight);
    updateHeight();

    return () => {
      window.removeEventListener('resize', updateHeight);
    };
  }, []);

  return (
    <SidebarListContext.Provider
      value={{
        rowIDs,
        activeContactID: activeContact?.id,
        setActiveContact,
        scrollIntoView,
        lastSelectionEventType,
        setLastSelectionEventType,
      }}
    >
      <VariableSizeList
        ref={listRef}
        height={height}
        itemCount={rowIDs.length}
        itemSize={(idx) => (idx === 0 ? 95 : 42)}
        width="100%"
      >
        {Row}
      </VariableSizeList>
    </SidebarListContext.Provider>
  );
}
